import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_KEYWORD_PINS } from './defaults';
import type { KeywordPin, NewKeywordPin } from './types';

/**
 * Persistence key and schema version for keyword pins.
 *
 * If the stored `version` does not match `PINS_SCHEMA_VERSION`, the stored
 * blob is discarded and `DEFAULT_KEYWORD_PINS` is re-seeded. No merge
 * attempt — versions are bumped intentionally to force a clean slate.
 *
 * TODO: when server-side persistence lands (see focused-view/pins/README.md
 * "Persistence" section), swap `readFromStorage` / `writeToStorage` for
 * `IUserPreferences`-backed calls. This hook's public API should stay stable.
 */
const STORAGE_KEY = 'focused-view:keyword-pins';
// v2: replaced default starter pins ("Competitor Intel", "Recap so far") with
// "Give me a hook" + "Give me a pitch". The version bump forces existing
// browsers to drop the cached v1 payload and reseed from `DEFAULT_KEYWORD_PINS`.
// Pins users explicitly created or edited under v1 are also discarded — we
// don't migrate today; persistent user customization is a follow-up.
const PINS_SCHEMA_VERSION = 2;

type StoredShape = {
	version: number;
	pins: KeywordPin[];
};

/**
 * Read pins from `localStorage`. Returns `null` on any failure (missing,
 * unparseable, wrong shape, wrong version, SSR environment) so the caller
 * can re-seed from defaults.
 */
function readFromStorage(): KeywordPin[] | null {
	try {
		if (typeof window === 'undefined' || !window.localStorage) {
			return null;
		}
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) {
			return null;
		}
		const parsed = JSON.parse(raw) as Partial<StoredShape>;
		if (!parsed || parsed.version !== PINS_SCHEMA_VERSION || !Array.isArray(parsed.pins)) {
			return null;
		}
		return parsed.pins as KeywordPin[];
	} catch {
		return null;
	}
}

/**
 * Write pins to `localStorage`. Swallows all errors — callers keep in-memory
 * state as the source of truth and tolerate persistence failures silently.
 */
function writeToStorage(pins: KeywordPin[]): void {
	try {
		if (typeof window === 'undefined' || !window.localStorage) {
			return;
		}
		const payload: StoredShape = { version: PINS_SCHEMA_VERSION, pins };
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
	} catch {
		// Intentionally swallowed — persistence is best-effort.
	}
}

/**
 * Generate a stable-ish id for newly created pins. Prefers `crypto.randomUUID`
 * when available; falls back to a timestamp + random suffix.
 */
function generatePinId(): string {
	try {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
			return `kp-${crypto.randomUUID()}`;
		}
	} catch {
		// Fall through to the deterministic fallback below.
	}
	return `kp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Stable sort: built-in pins first, then non-builtins. Insertion order is
 * preserved within each group.
 */
function orderPins(pins: ReadonlyArray<KeywordPin>): KeywordPin[] {
	const builtins: KeywordPin[] = [];
	const custom: KeywordPin[] = [];
	for (const pin of pins) {
		if (pin.builtin) {
			builtins.push(pin);
		} else {
			custom.push(pin);
		}
	}
	return [...builtins, ...custom];
}

/**
 * Take the user's stored pin list (already version-checked) and overlay it on
 * the current `DEFAULT_KEYWORD_PINS`. The result:
 *
 *   - **Untouched built-ins** (`userEdited` not set) are reseeded entirely
 *     from defaults — label, keyword, color, prompt, trigger. Code-side
 *     changes to any of those propagate to existing browsers immediately,
 *     without a schema bump. The user's `enabled` flag is the one thing
 *     preserved so toggling a pin off sticks.
 *   - **User-edited built-ins** (`userEdited === true`) are kept verbatim.
 *     Their customizations win and survive future default-pin changes;
 *     `userEdited` is set the first time the user touches a builtin via the
 *     editor (see `update()` below).
 *   - **Built-ins removed from `DEFAULT_KEYWORD_PINS`** are dropped — that's
 *     how "Competitor Intel" / "Recap so far" went away when they were
 *     retired from the defaults array.
 *   - **Non-builtin pins** the user created are kept verbatim.
 */
function rehydrateBuiltins(stored: KeywordPin[]): KeywordPin[] {
	const storedById = new Map(stored.filter(p => p.builtin).map(p => [p.id, p]));
	const builtins: KeywordPin[] = DEFAULT_KEYWORD_PINS.map(def => {
		const prior = storedById.get(def.id);
		if (!prior) return { ...def };
		if (prior.userEdited) return { ...prior };
		return {
			...def,
			enabled: prior.enabled,
		};
	});
	const custom = stored.filter(p => !p.builtin);
	return [...builtins, ...custom];
}

/** Public shape returned by `useKeywordPins`. */
export type UseKeywordPinsResult = {
	pins: KeywordPin[];
	create: (input: NewKeywordPin) => KeywordPin;
	update: (id: string, patch: Partial<KeywordPin>) => void;
	remove: (id: string) => void;
	toggleEnabled: (id: string) => void;
	matchKeyword: (text: string) => KeywordPin | null;
};

/**
 * React hook for the user's keyword pins.
 *
 * First read seeds from `DEFAULT_KEYWORD_PINS` if `localStorage` is empty or
 * stale. Subsequent mutations persist synchronously after each state update.
 *
 * All mutations go through React state so subscribers re-render; the
 * `localStorage` write is a side effect in a `useEffect`.
 */
export function useKeywordPins(): UseKeywordPinsResult {
	const [pins, setPins] = useState<KeywordPin[]>(() => {
		const stored = readFromStorage();
		if (stored && stored.length > 0) {
			// Reconcile against the latest `DEFAULT_KEYWORD_PINS` on every load
			// so retired/renamed builtins propagate without a schema bump.
			return orderPins(rehydrateBuiltins(stored));
		}
		return orderPins(DEFAULT_KEYWORD_PINS);
	});

	// Skip the initial persist — we either loaded from storage (no change) or
	// intentionally seeded defaults (fine to write once on first mount).
	const hasMountedRef = useRef(false);
	useEffect(() => {
		if (!hasMountedRef.current) {
			hasMountedRef.current = true;
			// Seed the store the first time so a fresh browser picks up defaults
			// without a user mutation.
			writeToStorage(pins);
			return;
		}
		writeToStorage(pins);
	}, [pins]);

	const create = useCallback((input: NewKeywordPin): KeywordPin => {
		const newPin: KeywordPin = {
			id: generatePinId(),
			label: input.label,
			keyword: input.keyword,
			color: input.color,
			prompt: input.prompt,
			trigger: input.trigger,
			enabled: input.enabled ?? true,
			builtin: false,
		};
		setPins(prev => orderPins([...prev, newPin]));
		return newPin;
	}, []);

	const update = useCallback((id: string, patch: Partial<KeywordPin>): void => {
		setPins(prev =>
			orderPins(
				prev.map(pin => {
					if (pin.id !== id) {
						return pin;
					}
					// Never allow `id` or `builtin` to be overwritten via patch.
					const { id: _ignoredId, builtin: _ignoredBuiltin, userEdited: _ignoredUserEdited, ...safePatch } = patch;
					// When the user opens the editor on a builtin and saves any
					// content change (label, keyword, color, prompt, trigger),
					// stamp `userEdited: true` so future code-side default
					// changes don't blow away their customization on reload.
					// Pure `enabled` toggles intentionally do NOT count as an
					// edit — they go through `toggleEnabled` and stay reversible.
					const isContentChange =
						'label' in safePatch ||
						'keyword' in safePatch ||
						'color' in safePatch ||
						'prompt' in safePatch ||
						'trigger' in safePatch;
					return {
						...pin,
						...safePatch,
						userEdited: pin.builtin && isContentChange ? true : pin.userEdited,
					};
				})
			)
		);
	}, []);

	const remove = useCallback((id: string): void => {
		setPins(prev => {
			const target = prev.find(pin => pin.id === id);
			if (!target || target.builtin) {
				// No-op: built-ins are not deletable; unknown ids are ignored.
				return prev;
			}
			return prev.filter(pin => pin.id !== id);
		});
	}, []);

	const toggleEnabled = useCallback((id: string): void => {
		setPins(prev => prev.map(pin => (pin.id === id ? { ...pin, enabled: !pin.enabled } : pin)));
	}, []);

	const matchKeyword = useCallback(
		(text: string): KeywordPin | null => {
			const normalized = text.toLowerCase().trim();
			if (!normalized) {
				return null;
			}
			for (const pin of pins) {
				if (!pin.enabled) {
					continue;
				}
				const keyword = pin.keyword.toLowerCase();
				if (!keyword) {
					continue;
				}
				if (normalized.startsWith(keyword)) {
					return pin;
				}
			}
			return null;
		},
		[pins]
	);

	return useMemo(
		() => ({ pins, create, update, remove, toggleEnabled, matchKeyword }),
		[pins, create, update, remove, toggleEnabled, matchKeyword]
	);
}
